import React, { createContext, useCallback, useContext, useState } from "react"

export type TimezoneKey = "europe-berlin" | "europe-vienna" | "europe-zurich" | "europe-london" | "us-eastern"
export type DateFormatKey = "dd.mm.yyyy" | "yyyy-mm-dd" | "mm/dd/yyyy"

export const TIMEZONE_IANA: Record<TimezoneKey, string> = {
    "europe-berlin": "Europe/Berlin",
    "europe-vienna": "Europe/Vienna",
    "europe-zurich": "Europe/Zurich",
    "europe-london": "Europe/London",
    "us-eastern": "America/New_York",
}

const LS_TZ_KEY = "aidocr_timezone"
const LS_FMT_KEY = "aidocr_date_format"

const DEFAULT_TZ: TimezoneKey = "europe-berlin"
const DEFAULT_FMT: DateFormatKey = "dd.mm.yyyy"

function readLS<T extends string>(key: string, fallback: T, valid: readonly T[]): T {
    const v = localStorage.getItem(key)
    return v && (valid as readonly string[]).includes(v) ? (v as T) : fallback
}

interface DateFormatContextValue {
    timezone: TimezoneKey
    dateFormat: DateFormatKey
    ianaTimezone: string
    setTimezone: (v: TimezoneKey) => void
    setDateFormat: (v: DateFormatKey) => void
    save: () => void
}

const DateFormatContext = createContext<DateFormatContextValue>({
    timezone: DEFAULT_TZ,
    dateFormat: DEFAULT_FMT,
    ianaTimezone: TIMEZONE_IANA[DEFAULT_TZ],
    setTimezone: () => {},
    setDateFormat: () => {},
    save: () => {},
})

const TIMEZONE_KEYS = ["europe-berlin", "europe-vienna", "europe-zurich", "europe-london", "us-eastern"] as const
const DATE_FORMAT_KEYS = ["dd.mm.yyyy", "yyyy-mm-dd", "mm/dd/yyyy"] as const

export function DateFormatProvider({ children }: { children: React.ReactNode }) {
    const [timezone, setTimezoneState] = useState<TimezoneKey>(() => readLS(LS_TZ_KEY, DEFAULT_TZ, TIMEZONE_KEYS))
    const [dateFormat, setDateFormatState] = useState<DateFormatKey>(() =>
        readLS(LS_FMT_KEY, DEFAULT_FMT, DATE_FORMAT_KEYS)
    )

    const setTimezone = useCallback((v: TimezoneKey) => {
        setTimezoneState(v)
        localStorage.setItem(LS_TZ_KEY, v)
    }, [])

    const setDateFormat = useCallback((v: DateFormatKey) => {
        setDateFormatState(v)
        localStorage.setItem(LS_FMT_KEY, v)
    }, [])

    const save = useCallback(() => {
        localStorage.setItem(LS_TZ_KEY, timezone)
        localStorage.setItem(LS_FMT_KEY, dateFormat)
    }, [timezone, dateFormat])

    return (
        <DateFormatContext.Provider
            value={{
                timezone,
                dateFormat,
                ianaTimezone: TIMEZONE_IANA[timezone],
                setTimezone,
                setDateFormat,
                save,
            }}>
            {children}
        </DateFormatContext.Provider>
    )
}

export function useDateFormatSettings() {
    return useContext(DateFormatContext)
}
